import { ClientCredentialsService } from './../client-credentials/client-credentials.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError, forkJoin } from 'rxjs';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BankAccountService } from './../profile-settings/bank-account.service';
import { environment } from '@env/environment';
import { ServiceHelpersService } from '../../_helpers/service-helpers.service';
import { ParamHandlingService } from '@services/misc-services/param-handling.service';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { CustomerContextService } from '@services/data-services/customer-context.service';

@Injectable(
  {
    providedIn: 'root',
  }
)
export class AuthenticationService {
  public token: string;

  constructor(
    private http: HttpClient,
    private serviceHelpers: ServiceHelpersService,
    private bankAccountService: BankAccountService,
    private clientCredentialsService: ClientCredentialsService,
    private paramHandlingService: ParamHandlingService,
    private acmicService: AcmicService,
    private customerContextService: CustomerContextService
  ) {
    // set token if saved in local storage
  }

  confirmPolicyAndAccount(userObject): Observable<any> {
    const user = userObject.$user.source.value,
      url = `${environment.backend_server_url_account}/${user.userDetails.email.address}`,
      userSendObject = this.serviceHelpers.creatUserObject(
        user,
        'createaccount'
      );

    return this.http.put(url, userSendObject, this.serviceHelpers.options).pipe(
      map((response) => response),
      catchError((err) => of(err))
    );
  }

  createPassword(userObject): Observable<any> {
    const user = userObject.$user.source.value,
      url = `${environment.backend_server_url_account}/guest/create`,
      userSendObject = this.serviceHelpers.creatUserObject(
        user,
        'createaccount'
      );
    return this.http.put(url, userSendObject, this.serviceHelpers.options);
  }

  activateAccount(userObject): Observable<any> {
    const queryParamsStr = this.paramHandlingService.getUtmQueryParamStr();
    let url = `${environment.backend_server_url_account}/activate?token=${userObject.token}`;
    if(this.acmicService.isACMIC() && queryParamsStr.length){
      url = `${environment.backend_server_url_account}/activate?token=${userObject.token}&${this.paramHandlingService.getUtmQueryParamStr()}`;
    }
    const btoaPass = window.btoa(userObject.password);
    const body = {
      email: userObject.email,
      password: btoaPass,
    };
    return this.http.put(url, body, this.serviceHelpers.options);
  }

  forgotPasswordSendEmailId(userEmail: string): Observable<any> {
    const url = `${environment.backend_server_url_identity_guest}/account-recovery`;

    return this.http.post(
      url,
      {},
      {
        params: { email: userEmail },
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  verifyForActiveAccount(email) {
    const url = `${environment.backend_server_url_identity}/${email}`;
    return this.http.get(url);
  }
 
  verifyForUserExists(email) {
    const url = `${environment.backend_server_url_identity_guest}/exist/${email}`;
    return this.http.get(url);
  }

  getUserDetailsByEmail(email) {
    const url = `${environment.backend_server_url_identity}/${email}`;

    return forkJoin(
      this.http.get(url),
      this.bankAccountService.getBankAccountByEmail(email)
    );
  }

  login(username: string, password: string) {
    const url = `${environment.backend_server_url_identity}/authenticate`;
    const btoaPass = window.btoa(password);
    const body = {
      email: username.toLowerCase(),
      password: btoaPass,
    };

    return this.clientCredentialsService.getToken().pipe(
      map((access_token) => {
        return this.http.post(url, body).pipe(
          map(
            (id) => {
              return JSON.stringify({ username, access_token, id });
            },
            catchError((error) => throwError(error))
          )
        );
      })
    );
  }

  loginBiometrics(username: string, password: string) {
    const url = `${environment.backend_server_url_identity_2}/login/authenticate`;
    const btoaPass = window.btoa(password);
    const body = {
      email: username.toLowerCase(),
      password: btoaPass,
    };

    return this.clientCredentialsService.getToken().pipe(
      map((access_token) => {
        return this.http.post(url, body).pipe(
          map(
            (id) => {
              return JSON.stringify({ username, access_token, id });
            },
            catchError((error) => throwError(error))
          )
        );
      })
    );
  }

  loginBiometricsToken(username: string, token: string) {
    const url = `${environment.backend_server_url_identity_2}/login/biometricsAuth`;
    const body = {
      token,
    };

    return this.clientCredentialsService.getToken().pipe(
      map((access_token) => {
        return this.http.post(url, body).pipe(
          map(
            (id) => {
              return JSON.stringify({ username, access_token, id });
            },
            catchError((error) => throwError(error))
          )
        );
      })
    );
  }

  logout(): void {
    this.token = null;
    this.customerContextService.clear();
    localStorage.removeItem('cpModalsnoozeSince');
    localStorage.removeItem('accountHolder');
    localStorage.removeItem('createuserinfo');
  }

  tokenVerification(token: string, email: string): Observable<object> {
    const url = `${environment.backend_server_url_identity_guest}/token-validation/${email}?token=${token}`;
    return this.http.post(url, {}, this.serviceHelpers.options);
  }

  updatePassword(user) {
    const url = `${environment.backend_server_url_identity_guest}/update-password`;
    const btoaPass = window.btoa(user.password);
    let oldPass = user.oldPass;
    if (user.oldPass != null || user.oldPass != undefined) {
      oldPass = window.btoa(user.oldPass);
    }
    const body = {
      email: user.email,
      password: oldPass,
      token: user.token,
      newPassword: btoaPass,
    };
    return this.http.post(url, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  verifyAccountTokenVerification(
    token: string,
    email: string
  ): Observable<object> {
    const url = `${environment.backend_server_url_account}/guest/activateCommercial?token=${token}&email=${email}`;
    return this.http.put(url, {}, this.serviceHelpers.options);
  }

  verifyPolicy(userObject): Observable<object> {
    const policyNumber = userObject.policyDetail.policynumber.policyNumber,
      url = `${environment.backend_server_url_account}/guest/v2/${policyNumber}/verify`,
      userSendObject = this.serviceHelpers.creatUserObject(
        userObject,
        'verifyuser'
      );
    return this.http.post(url, userSendObject);
  }

  verifyPolicyAndAdd(userObject, zipCode): Observable<object | string> {
    const policyNumber = userObject.policyDetail.policynumber.policyNumber?.toUpperCase(),
      url = `${environment.backend_server_url_account}/${policyNumber}/verifyAndAdd`,
      userSendObject = this.serviceHelpers.creatUserObject(
        userObject,
        'verifyAndAdd',
        zipCode
      );
    return this.http.post(url, userSendObject, { responseType: 'text' });
  }

  validateCustomer(userObject, policyNumber, captchaToken = '') {
    policyNumber = policyNumber.replace('-', '')?.toUpperCase();
    const user = userObject.$user.source.value,
      userSendObject = this.serviceHelpers.creatUserObject(
        user,
        'validateCustomer'
      ),
      url = `${environment.backend_server_url_account}/guest/v3/${policyNumber}/validateCustomer`;
    return this.http.post(url, userSendObject, {
      headers: {
        'X-Recaptcha-Token': captchaToken || '',
      },
    }).pipe(
      map((info: any) => info),
      catchError((error) => throwError(error))
    );
  }

  verifyUser(userObject): Observable<object> {
    const user = userObject.$user.source.value,
      userSendObject = this.serviceHelpers.creatUserObject(user, 'verifyuser'),
      url = `${environment.backend_server_url_account}/guest/${user.userDetails.email.address}`;

    return this.http
      .post(url, userSendObject, this.serviceHelpers.options)
      .pipe(
        map((info: any) => info),
        catchError((error) => throwError(error))
      );
  }

  checkLoginCount(email, count, flag): Observable<object> {
    const url = `${
      environment.backend_server_url_identity
    }/update-logon?email=${email.toLowerCase()}&count=${count}&flag=${flag}`;
    return this.http.put(url, {});
  }

  checkForLockedAccount(email: string): Observable<object> {
    const url = `${
      environment.backend_server_url_identity_guest
    }/check-locked/${email.toLowerCase()}`;
    return this.http.get(url);
  }

  validateZipAndEmail(zipCode, email, policyNumber, userObject) {
    const userSendObject = this.serviceHelpers.creatUserObject(
        userObject,
        'validateCustomer'
      ),
      url = `${environment.backend_server_url_account}/guest/v2/${policyNumber}/${zipCode}/${email}/validateZipAndEmail`;
    return this.http.post(url, userSendObject);
  }
}
